import type { ReactNode } from 'react'
import { useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import {
  paletteAccentMain,
  palettePrimaryMain,
  palettePrimaryMain as STAT_ICON_COLOR,
} from '@/theme/designTokens'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import { RiTargetFill } from 'react-icons/ri'

import campaignLaunchHeroIllustration from '@/assets/campaign-launch-hero-illustration.png'

import { formatCampaignWeekTimelineValue } from '../lib/campaign-week-timeline'
import { formatCampaignDurationRange } from '../lib/campaign-duration-display'
import { LAUNCH_SUMMARY_PANEL_GRADIENT } from '../lib/launch-panel-gradient'
import {
  smartFacetsToParagraph,
  smartFacetToParagraph,
} from '../lib/smart-facet-display'
import { useCompany } from '@/features/companies/api/company-queries'
import { useSessionStore } from '@/stores/useSessionStore'
import { useToast } from '@/shared/toast/ToastProvider'
import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'
import { useUpdateCampaignDuration } from '../api/campaign-mutations'
import { EditCampaignDurationDialog } from './EditCampaignDurationDialog'

import type { Campaign, ResearchGoalInterpretation } from '../types'

const STAT_CARD_BG = '#1F3455'
/** Figma 4383:3709 — icon chip on stat row */
const STAT_ICON_WELL_BG = 'rgba(0, 140, 208, 0.1)'
const STAT_ICON_WELL_RADIUS = '7px'
function isInterpretationShape(
  v: Campaign['researchGoalInterpretation']
): v is ResearchGoalInterpretation {
  return Boolean(v && typeof v === 'object' && 'primary_objective' in v)
}

function weekTimelineWeeks(campaign: Campaign): string {
  return formatCampaignWeekTimelineValue(campaign.startDate, campaign.endDate)
}

function formatBudget(budget: number | null): string {
  if (budget == null || !Number.isFinite(budget)) return '—'
  return budget.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function budgetWeeksLine(campaign: Campaign): string {
  const w = weekTimelineWeeks(campaign)
  if (w === '—') return 'Budget/—'
  const n = Number.parseInt(w, 10)
  if (Number.isNaN(n)) return `Budget/${w} weeks`
  return n === 1 ? 'Budget/1 week' : `Budget/${n} weeks`
}

/** Figma hero — three equal-width stat chips in one row */
const heroStatCardSx = {
  flex: { xs: '1 1 auto', sm: '1 1 0' },
  minWidth: { xs: 0, sm: 0 },
  maxWidth: { xs: '100%', sm: 'none' },
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  minHeight: 54,
  borderRadius: '8px',
  bgcolor: STAT_CARD_BG,
} as const

function targetAudienceParagraph(campaign: Campaign): string {
  const raw = campaign.targetAudience.replaceAll('\r\n', '\n').trim()
  return raw || '—'
}

/** Section row icons — slightly larger than the 14px title. */
const SECTION_ICON_PX = 26

const sectionTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: paletteAccentMain,
  mb: 0.5,
} as const

const sectionBodySx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#FFFFFF',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
} as const

function OverviewSection({
  icon,
  title,
  children,
  testId,
}: Readonly<{
  icon: ReactNode
  title: string
  children: ReactNode
  testId?: string
}>) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      data-testid={testId}
    >
      <Box
        aria-hidden
        sx={{
          color: paletteAccentMain,
          mt: '1px',
          flexShrink: 0,
          display: 'flex',
          width: SECTION_ICON_PX,
          height: SECTION_ICON_PX,
          '& .MuiSvgIcon-root, & svg': {
            fontSize: SECTION_ICON_PX,
            width: SECTION_ICON_PX,
            height: SECTION_ICON_PX,
          },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={sectionTitleSx}>{title}</Typography>
        {children}
      </Box>
    </Stack>
  )
}

interface Props {
  campaign: Campaign
}

export function LaunchCampaignOverviewCard({
  campaign,
}: Readonly<Props>) {
  const companyId = campaign.companyId?.trim() || ''
  const { data: company } = useCompany(companyId)
  const companyName = company?.name?.trim() || ''
  const showCompany = Boolean(companyId && companyName)

  const interp = campaign.researchGoalInterpretation
  const targetAudience = targetAudienceParagraph(campaign)
  const primaryObjective = isInterpretationShape(interp)
    ? smartFacetToParagraph(interp.primary_objective)
    : ''
  const secondaryObjective = isInterpretationShape(interp)
    ? smartFacetsToParagraph(interp.secondary_objectives ?? [])
    : ''

  const trimmedResearchGoal = campaign.researchGoal.trim()

  const persistedLoiCount = campaign.linesOfInquiry?.length ?? 0
  const fallbackRecommend = isInterpretationShape(interp)
    ? (interp.recommended_loi_count ?? 0)
    : 0
  const displayLoiCount =
    persistedLoiCount > 0 ? persistedLoiCount : fallbackRecommend

  const [durationOpen, setDurationOpen] = useState(false)
  const role = useSessionStore((s) => s.user?.role)
  const { showToast } = useToast()
  const updateDuration = useUpdateCampaignDuration()
  const canEditDuration = role === 'ADMIN' && campaign.status !== 'completed'

  return (
    <>
    <Box
      data-testid="launch-campaign-overview-card"
      sx={{
        position: 'relative',
        borderRadius: '10px',
        overflow: 'hidden',
        mb: '32px',
        bgcolor: '#041E42',
      }}
    >
      {/* Figma bg/02: rotated gradient + subtle centered radial cyan (not a large blur blob) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
          borderRadius: '10px',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            transform: 'rotate(-90deg)',
            transformOrigin: 'top left',
            bgcolor: '#041E42',
            backgroundImage: LAUNCH_SUMMARY_PANEL_GRADIENT,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
          }}
        />
        {/* Subtle cyan mist — tighter radial than a large blurred div (matches Urban Futures reference) */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '10px',
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0, 205, 228, 0.1) 0%, rgba(0, 205, 228, 0.03) 52%, transparent 68%)',
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, md: '35px' },
          py: { xs: 2, md: '20px' },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: '17px' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        >
          <Box
            component="img"
            src={campaignLaunchHeroIllustration}
            alt=""
            data-testid="launch-campaign-hero-illustration"
            sx={{
              width: { xs: 72, sm: 58 },
              height: { xs: 72, sm: 58 },
              flexShrink: 0,
              objectFit: 'cover',
              borderRadius: '8px',
              display: 'block',
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: { xs: 24, sm: '32px' },
                lineHeight: { xs: '32px', sm: '40px' },
                letterSpacing: '0.40625px',
                color: paletteAccentMain,
                mb: showCompany ? 0.75 : 0,
              }}
            >
              {campaign.name.trim() || 'Campaign'}
            </Typography>
            {showCompany ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                data-testid="campaign-launch-company"
              >
                <BusinessOutlinedIcon
                  sx={{ fontSize: 20, color: '#FFFFFF' }}
                  aria-hidden
                />
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#FFFFFF',
                  }}
                >
                  {companyName}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Stack>

        <Box
          aria-hidden
          data-testid="launch-overview-header-divider"
          sx={{
            mt: 2,
            mb: 2.5,
            height: '1px',
            bgcolor: 'rgba(15, 212, 252, 0.28)',
          }}
        />

        <Stack spacing={2.5} sx={{ mb: 2.5 }}>
          <OverviewSection
            icon={
              <RiTargetFill
                size={SECTION_ICON_PX}
                data-testid="launch-overview-goal-icon"
              />
            }
            title="Campaign Goal"
            testId="launch-overview-campaign-goal"
          >
            <Typography component="p" sx={sectionBodySx}>
              {trimmedResearchGoal === ''
                ? 'No research goal captured for this campaign.'
                : trimmedResearchGoal}
            </Typography>
          </OverviewSection>

          {primaryObjective === '' ? null : (
            <OverviewSection
              icon={<FlagOutlinedIcon />}
              title="Primary Objective"
              testId="launch-overview-primary-objective"
            >
              <Typography sx={sectionBodySx}>{primaryObjective}</Typography>
            </OverviewSection>
          )}

          {secondaryObjective === '' ? null : (
            <OverviewSection
              icon={<SearchOutlinedIcon />}
              title="Secondary Objective"
              testId="launch-overview-secondary-objective"
            >
              <Typography sx={sectionBodySx}>{secondaryObjective}</Typography>
            </OverviewSection>
          )}

          <OverviewSection
            icon={<PeopleOutlineIcon />}
            title="Target Audience"
            testId="launch-overview-target-audience"
          >
            <Typography
              data-testid="launch-target-audience-paragraph"
              sx={sectionBodySx}
            >
              {targetAudience}
            </Typography>
          </OverviewSection>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            width: '100%',
            mt: 1,
          }}
        >
          <Box sx={heroStatCardSx}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: STAT_ICON_WELL_RADIUS,
                bgcolor: STAT_ICON_WELL_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  color: STAT_ICON_COLOR,
                  fontSize: 20,
                },
              }}
              aria-hidden
            >
              <AccountBalanceWalletOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: `var(--b-primary-1, ${palettePrimaryMain})`,
                }}
              >
                {campaign.budget != null && Number.isFinite(campaign.budget)
                  ? `$${formatBudget(campaign.budget)}`
                  : '—'}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#FFF',
                }}
              >
                {budgetWeeksLine(campaign)}
              </Typography>
            </Box>
          </Box>

          <Box sx={heroStatCardSx}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: STAT_ICON_WELL_RADIUS,
                bgcolor: STAT_ICON_WELL_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  color: STAT_ICON_COLOR,
                  fontSize: 20,
                },
              }}
              aria-hidden
            >
              <AssignmentOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: `var(--b-primary-1, ${palettePrimaryMain})`,
                }}
              >
                {displayLoiCount}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#FFF',
                }}
              >
                Lines of Inquiry
              </Typography>
            </Box>
          </Box>

          <Box sx={heroStatCardSx} data-testid="campaign-duration-tile">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: STAT_ICON_WELL_RADIUS,
                bgcolor: STAT_ICON_WELL_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  color: STAT_ICON_COLOR,
                  fontSize: 20,
                },
              }}
              aria-hidden
            >
              <CalendarMonthOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: `var(--b-primary-1, ${palettePrimaryMain})`,
                }}
              >
                Campaign Duration
              </Typography>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#FFF',
                }}
              >
                {formatCampaignDurationRange(campaign.startDate, campaign.endDate)}
              </Typography>
            </Box>
            {canEditDuration ? (
              <IconButton
                aria-label="Edit campaign duration"
                onClick={() => setDurationOpen(true)}
                data-testid="campaign-duration-edit"
                sx={{
                  color: STAT_ICON_COLOR,
                  p: 0.5,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'rgba(0, 140, 208, 0.12)' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
    <EditCampaignDurationDialog
      open={durationOpen}
      startDate={campaign.startDate}
      endDate={campaign.endDate}
      saving={updateDuration.isPending}
      onClose={() => setDurationOpen(false)}
      onSave={async (endDate) => {
        try {
          await updateDuration.mutateAsync({
            campaignId: campaign.id,
            endDate,
          })
          showToast('Campaign duration updated', 'success')
          setDurationOpen(false)
        } catch (err) {
          showToast(
            messageFromUnknownApiError(err, 'Could not update campaign duration'),
            'error',
          )
        }
      }}
    />
    </>
  )
}
